/**
 * Rules that check what a graphic's shapes *claim*. A chart is the one thing in a motion graphic a
 * viewer takes away as fact, and hand-typed bar heights and curve vertices look exactly as
 * convincing as derived ones — so a chart declares its figures with `data-l5-values` and these rules
 * check the drawn shape against them.
 */
import { describeElement } from './measure';
import type { ElementMeasurement, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

const OPACITY_PRESENT_THRESHOLD = 0.05;
/** How far a drawn shape may sit from what it claims to encode, as a fraction of the largest. */
const ENCODING_TOLERANCE = 0.05;
/** Fewer shapes than this under one parent is a pair of blocks, not a series. */
const MIN_SERIES_SHAPES = 3;
/** At this share of the canvas an element is the frame the graphic sits in, not a chart in it. */
const FULL_CANVAS_FRACTION = 0.9;
/** Bars stand on one line; this is rounding on that line, not a second one. */
const SHARED_EDGE_TOL_PX = 4;

const VALUES_ATTR = 'data-l5-values';
const NOT_DATA_ATTR = '[data-l5-not-data]';

/** The declared series on an element, or null when it declares none (or declares nonsense). */
export function declaredValues(element: Element): number[] | null {
    const raw = element.getAttribute(VALUES_ATTR);
    if (raw === null) return null;
    const values = raw
        .split(',')
        .map((part) => parseFloat(part.trim()))
        .filter((value) => Number.isFinite(value));
    return values.length > 0 ? values : null;
}

/**
 * The vertex points of a path's `d`, in its own coordinate space. Only the explicit coordinate pairs
 * of absolute commands are read: this is a check on the shape's overall rise and fall, and a curve's
 * control points bend the line between vertices without being vertices themselves.
 */
export function pathVertices(element: Element): Array<{ x: number; y: number }> {
    const d = element.getAttribute('d');
    if (!d) return [];
    const vertices: Array<{ x: number; y: number }> = [];
    const command = /([MLT])\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/g;
    let match: RegExpExecArray | null;
    while ((match = command.exec(d)) !== null) {
        vertices.push({ x: parseFloat(match[2]), y: parseFloat(match[3]) });
    }
    return vertices;
}

/**
 * Whether these sibling shapes read as a series: enough of them, at enough distinct lengths, and
 * standing on one line. The shared edge is what separates a bar set from any other group of
 * differently-sized shapes — a ring of tick marks and the parts of an icon vary in length too, and
 * reporting those as undeclared charts is what teaches an author to skip the rule.
 */
export function isSeriesOfShapes(shapes: ElementMeasurement[]): boolean {
    if (shapes.length < MIN_SERIES_SHAPES) return false;
    const aligned = (lengths: number[], edges: number[]): boolean =>
        new Set(lengths.map(Math.round)).size >= MIN_SERIES_SHAPES &&
        Math.max(...edges) - Math.min(...edges) <= SHARED_EDGE_TOL_PX;
    const columns = aligned(
        shapes.map((m) => m.rect.height),
        shapes.map((m) => m.rect.y + m.rect.height),
    );
    const rows = aligned(
        shapes.map((m) => m.rect.width),
        shapes.map((m) => m.rect.x),
    );
    return columns || rows;
}

/**
 * Whether this element is the frame the graphic sits in rather than something drawn in it. Every
 * composition has a full-canvas wrapper holding a few elements of different sizes, which is the
 * shape of a bar set and is never a bar set — and a rule that reports the root on every graphic is
 * a rule an author learns to skip.
 */
export function isCompositionFrame(
    rect: Rect,
    canvasSize?: { width: number; height: number },
): boolean {
    if (!canvasSize) return false;
    return (
        rect.width >= canvasSize.width * FULL_CANVAS_FRACTION &&
        rect.height >= canvasSize.height * FULL_CANVAS_FRACTION
    );
}

/**
 * Whether this element draws a data series: a declaration with shapes to match it, or a bar set
 * recognisable without one. A declaration alone is not enough — a counter declares the figure it
 * counts up to, and a unit field the count it fills — and treating those as charts sends their
 * authors off to stand a hero number on the plot baseline.
 */
export function drawsSeries(element: Element, elements: ElementMeasurement[]): boolean {
    if (isMarkedDecorative(element)) return false;
    const values = declaredValues(element);
    if (
        values &&
        values.length >= 2 &&
        candidateGroups(element, values.length, elements).groups.length
    ) {
        return true;
    }
    return isSeriesOfShapes(shapeChildren(element, elements));
}

/** Whether this element or anything inside it puts words on screen. */
function rendersText(element: Element, elements: ElementMeasurement[]): boolean {
    return elements.some(
        (m) => m.hasText && (m.element === element || element.contains(m.element)),
    );
}

/** An element the author has marked as carrying no data — a staircase motif, equaliser bars. */
export function isMarkedDecorative(element: Element): boolean {
    return Boolean(element.closest(NOT_DATA_ATTR));
}

/**
 * Every probe's visible measurements, last frame first. A chart is judged across all of them: a
 * staggered entrance still in flight at the last probe measures its bars mid-tween, and a chart that
 * has left before then is not measured at all — judging by one frame turns both into verdicts about
 * the frame rather than about the chart.
 */
function measuredFrames(context: RuntimeLintContext): ElementMeasurement[][] {
    return [...(context.frames ?? [])]
        .reverse()
        .map((frame) => frame.elements.filter((m) => m.opacity >= OPACITY_PRESENT_THRESHOLD))
        .filter((elements) => elements.length > 0);
}

/** The shapes inside `element` that read as a series, at whatever depth they sit. */
function seriesInside(
    element: Element,
    elements: ElementMeasurement[],
): ElementMeasurement[] | null {
    const direct = shapeChildren(element, elements);
    if (isSeriesOfShapes(direct)) return direct;
    const byDepth = new Map<number, ElementMeasurement[]>();
    for (const measurement of elements) {
        const candidate = measurement.element;
        if (
            !isDrawnShape(measurement, elements) ||
            candidate === element ||
            !element.contains(candidate)
        ) {
            continue;
        }
        let depth = 0;
        for (let cur = candidate.parentElement; cur && cur !== element; cur = cur.parentElement) {
            depth++;
        }
        byDepth.set(depth, [...(byDepth.get(depth) ?? []), measurement]);
    }
    for (const bucket of byDepth.values()) {
        if (isSeriesOfShapes(bucket)) return bucket;
    }
    return null;
}

/**
 * A series declared one figure per element: sibling shapes that each carry a single value. Written
 * that way a "1,900 of 2,400" is two bars, each labelled with its own figure — legible markup, and
 * no less checkable than one container holding both, as long as the check looks across the siblings
 * rather than inside each of them.
 */
function siblingSeries(
    element: Element,
    elements: ElementMeasurement[],
): { shapes: ElementMeasurement[]; values: number[] } | null {
    const own = declaredValues(element);
    if (!own || own.length !== 1) return null;
    const parent = element.parentElement;
    if (!parent) return null;

    const shapes: ElementMeasurement[] = [];
    const values: number[] = [];
    for (const measurement of elements) {
        if (measurement.element.parentElement !== parent || !isDrawnShape(measurement, elements))
            continue;
        const declared = declaredValues(measurement.element);
        if (!declared || declared.length !== 1) continue;
        shapes.push(measurement);
        values.push(declared[0]);
    }
    return shapes.length >= 2 ? { shapes, values } : null;
}

/**
 * The sibling series read in the frame that shows it landed. Same reason as `bestFrameFor`: an
 * entrance or an exit still in flight at one probe measures tween lengths, and reporting those as
 * the chart's proportions blocks a write on a chart that is drawn correctly.
 */
function bestSiblingFrame(
    element: Element,
    frames: ElementMeasurement[][],
): { shapes: ElementMeasurement[]; values: number[] } | null {
    let fallback: { shapes: ElementMeasurement[]; values: number[] } | null = null;
    for (const elements of frames) {
        const series = siblingSeries(element, elements);
        if (!series) continue;
        if (encodesProportionally(lengthsOf(series.shapes), series.values)) return series;
        fallback = fallback ?? series;
    }
    return fallback;
}

/** The frame a declared element is best judged in: the one where its shapes encode its values. */
function bestFrameFor(
    element: Element,
    values: number[],
    frames: ElementMeasurement[][],
): { elements: ElementMeasurement[]; encoding: Encoding } | null {
    let fallback: { elements: ElementMeasurement[]; encoding: Encoding } | null = null;
    for (const elements of frames) {
        if (!elements.some((m) => m.element === element)) continue;
        const encoding = encodingOf(element, values, elements);
        if (encoding?.kind === 'bars' && encodesProportionally(encoding.lengths, values)) {
            return { elements, encoding };
        }
        if (encoding?.kind === 'path' && stepsProportionally(encoding.vertexYs, values)) {
            return { elements, encoding };
        }
        fallback = fallback ?? { elements, encoding };
    }
    return fallback;
}

/**
 * SVG nodes that describe paint rather than draw it. They measure as zero-area boxes with no text,
 * so counting them as shapes miscounts a series — the bars plus a `<defs>` is four shapes, not
 * three, and the group no longer matches the figures declared beside it.
 */
const NON_DRAWING_TAGS = new Set([
    'DEFS',
    'LINEARGRADIENT',
    'RADIALGRADIENT',
    'STOP',
    'CLIPPATH',
    'MASK',
    'PATTERN',
    'FILTER',
    'MARKER',
    'SYMBOL',
    'DESC',
    'METADATA',
]);

/**
 * Whether this measurement is something drawn: not copy, not scaffolding, and actually on screen.
 *
 * "Not copy" is read broadly — anything with words inside it, not only an element rendering its own
 * text. A stat card, a callout and a labelled bar are indistinguishable from the outside: each is a
 * box whose size comes from the type in it. Measuring those against declared figures produced a
 * false blocking error in three of four real builds, and a block this rule cannot justify is worse
 * than a chart it never checks — once the agent cleared one by stripping the declaration, which
 * left the scene blank. So a bar that prints its own label goes unchecked, deliberately.
 */
function isDrawnShape(measurement: ElementMeasurement, elements: ElementMeasurement[]): boolean {
    if (measurement.hasText) return false;
    if (rendersText(measurement.element, elements)) return false;
    // An axis, a gridline or a baseline marked as carrying no data is not one of the series' shapes.
    if (isMarkedDecorative(measurement.element)) return false;
    if (NON_DRAWING_TAGS.has(measurement.element.tagName.toUpperCase())) return false;
    // A bar for a zero value has no height and is still one of the series' shapes; scaffolding is
    // the thing with no box at all.
    return measurement.rect.width > 0 || measurement.rect.height > 0;
}

/** The direct children of `element` that are drawn shapes rather than copy. */
export function shapeChildren(
    element: Element,
    elements: ElementMeasurement[],
): ElementMeasurement[] {
    return elements.filter((m) => isDrawnShape(m, elements) && m.element.parentElement === element);
}

/**
 * Whether `series` encodes `values` — each shape's length in the same ratio to the largest as its
 * value is to the largest value. Bar length *is* the quantity, so this is proportionality through
 * the origin: a bar half as long as its neighbour claims half the value, whatever the axis says.
 */
function encodesProportionally(series: number[], values: number[]): boolean {
    const maxLength = Math.max(...series);
    // Magnitudes, because a length is never negative: a chart of losses draws how big each loss is
    // and says which way it goes by where it sits.
    const magnitudes = values.map(Math.abs);
    const maxValue = Math.max(...magnitudes);
    if (maxLength <= 0 || maxValue <= 0) return false;
    return series.every(
        (length, i) =>
            Math.abs(length / maxLength - magnitudes[i] / maxValue) <= ENCODING_TOLERANCE,
    );
}

/**
 * Whether a line's vertices track `values`. Unlike bars this is checked on the *steps* between
 * vertices rather than their distance from an axis: a line chart may legitimately start its scale
 * above zero, so what has to hold is one consistent px-per-unit across every step.
 */
function stepsProportionally(vertexYs: number[], values: number[]): boolean {
    const steps: number[] = [];
    const deltas: number[] = [];
    for (let i = 1; i < vertexYs.length; i++) {
        // y grows downward, so a rising value is a falling y.
        steps.push(vertexYs[i - 1] - vertexYs[i]);
        deltas.push(values[i] - values[i - 1]);
    }
    const maxStep = Math.max(...steps.map(Math.abs));
    const maxDelta = Math.max(...deltas.map(Math.abs));
    // A series that does not move is drawn correctly by a line that does not move.
    if (maxDelta === 0) return maxStep === 0;
    if (maxStep <= 0) return false;
    return steps.every(
        (step, i) => Math.abs(step / maxStep - deltas[i] / maxDelta) <= ENCODING_TOLERANCE,
    );
}

function ratios(series: number[]): string {
    const max = Math.max(...series.map(Math.abs));
    return series.map((value) => (max > 0 ? (Math.abs(value) / max).toFixed(2) : '0')).join(', ');
}

/**
 * How a declared element's shape encodes its values, or null when no reading applies.
 *
 * The shapes are looked for anywhere inside the declared container, not just among its direct
 * children: the ordinary column structure wraps each bar with its value and its label, so the
 * container's own children are fixed-height wrappers and only their grandchildren encode anything.
 * Every sibling group of the right size is a candidate, and a group that encodes the values wins —
 * the question being asked is whether the declaration is drawn *somewhere* in here, and a group
 * that answers it yes is the answer.
 *
 * Length is read on whichever axis varies, so a horizontal bar set is judged by width the way a
 * column set is judged by height.
 */
type Encoding =
    | { kind: 'bars'; shapes: ElementMeasurement[]; lengths: number[] }
    | { kind: 'path'; path: Element; vertexYs: number[] }
    | null;

/**
 * The groups of shapes inside `element` that could be its series: every depth below it holding
 * exactly `size` shapes. Depth rather than parentage, because the column structure gives each bar
 * its own wrapper — the bars are then siblings of nothing, and the only thing they share is how far
 * down they sit.
 */
function candidateGroups(
    element: Element,
    size: number,
    elements: ElementMeasurement[],
): { groups: ElementMeasurement[][]; exact: ElementMeasurement[][] } {
    const byDepth = new Map<number, ElementMeasurement[]>();
    for (const measurement of elements) {
        const candidate = measurement.element;
        if (
            !isDrawnShape(measurement, elements) ||
            candidate === element ||
            !element.contains(candidate)
        ) {
            continue;
        }
        let depth = 0;
        for (let cur = candidate.parentElement; cur && cur !== element; cur = cur.parentElement) {
            depth++;
        }
        const group = byDepth.get(depth) ?? [];
        group.push(measurement);
        byDepth.set(depth, group);
    }
    const groups: ElementMeasurement[][] = [];
    const exact: ElementMeasurement[][] = [];
    for (const bucket of byDepth.values()) {
        if (bucket.length === size) {
            groups.push(bucket);
            exact.push(bucket);
            continue;
        }
        // A baseline or an axis rule drawn beside the bars makes the bucket one too many. The
        // series is still in there, so offer each run of the right length rather than giving up —
        // giving up is what silently stops the error check on a chart that is derived correctly.
        for (let start = 0; start + size <= bucket.length; start++) {
            groups.push(bucket.slice(start, start + size));
        }
    }
    return { groups, exact };
}

/**
 * Whether a series that crosses zero is drawn on both sides of one line. Lengths are magnitudes —
 * a bar cannot be negative — so direction is the only thing left that says which way a figure went,
 * and a loss grown upward from the same anchor as a gain reads as a gain. Checked only for a mixed
 * series: an all-negative one labelled "losses" is legitimately drawn as lengths.
 */
function drawsSignsApart(shapes: ElementMeasurement[], values: number[]): boolean {
    const negatives = shapes.filter((_, i) => values[i] < 0);
    const positives = shapes.filter((_, i) => values[i] > 0);
    if (negatives.length === 0 || positives.length === 0) return true;
    // Both groups touch the zero line, from opposite sides: columns meet it with a positive's foot
    // and a negative's head, a diverging row with a positive's left edge and a negative's right.
    const edges =
        quantityAxis(shapes) === 'width'
            ? [...positives.map((m) => m.rect.x), ...negatives.map((m) => m.rect.x + m.rect.width)]
            : [
                  ...positives.map((m) => m.rect.y + m.rect.height),
                  ...negatives.map((m) => m.rect.y),
              ];
    return Math.max(...edges) - Math.min(...edges) <= SHARED_EDGE_TOL_PX;
}

/** The axis that carries the quantity: the one whose lengths actually differ. */
function quantityAxis(shapes: ElementMeasurement[]): 'width' | 'height' {
    const spread = (values: number[]): number => Math.max(...values) - Math.min(...values);
    return spread(shapes.map((m) => m.rect.width)) > spread(shapes.map((m) => m.rect.height))
        ? 'width'
        : 'height';
}

function lengthsOf(shapes: ElementMeasurement[]): number[] {
    const axis = quantityAxis(shapes);
    return shapes.map((m) => (axis === 'width' ? m.rect.width : m.rect.height));
}

function encodingOf(element: Element, values: number[], elements: ElementMeasurement[]): Encoding {
    if (values.length < 2) return null;
    const { groups, exact } = candidateGroups(element, values.length, elements);
    const encoding = groups.find((shapes) => encodesProportionally(lengthsOf(shapes), values));
    if (encoding) {
        return { kind: 'bars', shapes: encoding, lengths: lengthsOf(encoding) };
    }
    // Not the first path: a line chart with an area fill under it paints the fill first, and the
    // fill's `d` closes down to the baseline, so its vertex count never matches.
    for (const path of Array.from(element.querySelectorAll('path'))) {
        const vertices = pathVertices(path);
        if (vertices.length === values.length) {
            return { kind: 'path', path, vertexYs: vertices.map((v) => v.y) };
        }
    }
    // Nothing encodes them. Report only where the shapes were counted out one per figure — a
    // different number of shapes is a different mistake, and this rule would be guessing which.
    const drawn = [...exact].sort((a, b) => {
        const spread = (group: ElementMeasurement[]): number => {
            const lengths = lengthsOf(group);
            return Math.max(...lengths) - Math.min(...lengths);
        };
        return spread(b) - spread(a);
    })[0];
    return drawn ? { kind: 'bars', shapes: drawn, lengths: lengthsOf(drawn) } : null;
}

export const chartValuesMismatch: RuntimeLintRule = {
    id: 'chart-values-mismatch',
    severity: 'error',
    guidance:
        "Derive a chart's geometry from its declared values: compute bar heights and path " +
        'vertices from that array in the script, never type them. A shape that does not encode ' +
        'the values it claims is a fabricated chart, and a chart is the one thing on screen a ' +
        'viewer takes as fact.',
    check(context: RuntimeLintContext): string[] {
        const frames = measuredFrames(context);
        const messages: string[] = [];
        const seen = new Set<Element>();

        for (const elements of frames) {
            for (const { element } of elements) {
                if (seen.has(element) || isMarkedDecorative(element)) continue;
                const values = declaredValues(element);
                if (!values) continue;
                seen.add(element);

                const siblings = bestSiblingFrame(element, frames);
                if (siblings) {
                    // Reported against the first of the group, so one series is one finding.
                    siblings.shapes.forEach(({ element: member }) => seen.add(member));
                    const lengths = lengthsOf(siblings.shapes);
                    if (encodesProportionally(lengths, siblings.values)) continue;
                    messages.push(
                        `${describeElement(element)} and its ${siblings.shapes.length - 1} ` +
                            `declaring sibling(s) carry ${siblings.values.join(', ')} but are drawn ` +
                            `at ${ratios(lengths)} of the largest, where the values are ` +
                            `${ratios(siblings.values)}. Derive each one's length from the figure ` +
                            `it declares.`,
                    );
                    continue;
                }

                const best = bestFrameFor(element, values, frames);
                const encoding = best?.encoding;
                if (!encoding) continue;

                if (encoding.kind === 'bars') {
                    if (!drawsSignsApart(encoding.shapes, values)) {
                        messages.push(
                            `${describeElement(element)} declares ${values.join(', ')}, which ` +
                                `crosses zero, but every shape is drawn the same way from one ` +
                                `edge — so a fall reads as a rise. Anchor the series to a zero ` +
                                `line and let the negative figures hang below it.`,
                        );
                        continue;
                    }
                    if (encodesProportionally(encoding.lengths, values)) continue;
                    const encodesNothing = new Set(encoding.lengths.map(Math.round)).size === 1;
                    messages.push(
                        `${describeElement(element)} declares ${values.join(', ')} but its ` +
                            `${encoding.lengths.length} shapes are drawn at ` +
                            `${ratios(encoding.lengths)} of the largest, where the values are ` +
                            `${ratios(values)}. ` +
                            (encodesNothing
                                ? `Shapes all one length encode nothing, so this is decoration ` +
                                  `wearing a chart's attribute: mark it data-l5-not-data and drop ` +
                                  `the values, or make it a real chart by deriving each height ` +
                                  `from the declared array.`
                                : `Compute each height from the declared array ` +
                                  `(\`const values = el.dataset.l5Values.split(',').map(Number)\`) ` +
                                  `instead of typing it.`),
                    );
                    continue;
                }

                if (!stepsProportionally(encoding.vertexYs, values)) {
                    messages.push(
                        `${describeElement(element)} declares ${values.join(', ')}, but the ` +
                            `vertices of ${describeElement(encoding.path)} do not step in ` +
                            `proportion to them — the drawn line says something the data does not. ` +
                            `Generate the \`d\` string from the declared array rather than typing ` +
                            `its points.`,
                    );
                }
            }
        }
        return messages;
    },
};

export const chartValuesMissing: RuntimeLintRule = {
    id: 'chart-values-missing',
    severity: 'warning',
    guidance:
        'A bar set declares what it plots: put the real figures on the container as ' +
        '`data-l5-values="v1,v2,…"` so the shape can be checked against them. A plotted line wants ' +
        'the same treatment, though nothing asks for it — from the outside a series and a drawn ' +
        'route are the same polyline. Mark a bar-like shape that carries no data — a staircase ' +
        'motif, equaliser bars — `data-l5-not-data`.',
    check(context: RuntimeLintContext): string[] {
        const frames = measuredFrames(context);
        const messages: string[] = [];
        const reported = new Set<Element>();

        // Presence is not a declaration: `data-l5-values="TBD"` would otherwise silence this rule
        // and every check downstream of it, on exactly the hand-drawn chart they exist for.
        const declared = (element: Element): boolean => {
            if (element.closest(NOT_DATA_ATTR)) return true;
            for (
                let current: Element | null = element.closest(`[${VALUES_ATTR}]`);
                current;
                current = current.parentElement?.closest(`[${VALUES_ATTR}]`) ?? null
            ) {
                if (declaredValues(current)) return true;
            }
            return false;
        };

        // Only the settled frame — the last one each element appears in. Mid-entrance, a row of
        // identical blocks staggering in has three distinct lengths on one baseline, which is the
        // shape of a bar set and is not one.
        for (const elements of frames.slice(0, 1)) {
            // A series is looked for at every depth, since the column layout gives each bar its own
            // wrapper; the innermost container that holds one is the one worth naming, so an
            // element whose descendant also holds a series is left to that descendant.
            const holders = new Map<Element, ElementMeasurement[]>();
            for (const { element, rect } of elements) {
                if (declared(element) || isCompositionFrame(rect, context.canvasSize)) continue;
                const series = seriesInside(element, elements);
                if (series) holders.set(element, series);
            }
            for (const [element, series] of holders) {
                if (reported.has(element)) continue;
                if (
                    [...holders.keys()].some(
                        (other) => other !== element && element.contains(other),
                    )
                )
                    continue;
                reported.add(element);
                messages.push(
                    `${describeElement(element)} reads as a chart — ${series.length} shapes at ` +
                        `different lengths — but declares no data. Put its figures on the ` +
                        `container as data-l5-values="…" and derive the lengths from them, or mark ` +
                        `it data-l5-not-data if the shapes carry no data.`,
                );
            }
        }
        return messages;
    },
};

/**
 * A declaration nothing can check. The shape rules read a series as one shape per figure or one
 * vertex per figure; a figure declared on its own element, or a proportion drawn as a single fill,
 * satisfies the attribute and is then drawn on trust — which is the state declaring values exists to
 * end. A figure the graphic only ever *writes out* is exempt: that is copy, and the plan's text
 * check already covers it.
 */
export const chartValuesUnverifiable: RuntimeLintRule = {
    id: 'chart-values-unverifiable',
    severity: 'warning',
    guidance:
        'Keep a series on one container, with one shape or one path vertex per figure, so its ' +
        'shape can be checked against it. Figures spread one per element, or a proportion drawn ' +
        'as a single fill, declare what nothing can verify.',
    check(context: RuntimeLintContext): string[] {
        const frames = measuredFrames(context);
        const messages: string[] = [];
        const seen = new Set<Element>();

        for (const elements of frames) {
            for (const { element } of elements) {
                if (seen.has(element)) continue;
                const values = declaredValues(element);
                if (!values || isMarkedDecorative(element)) continue;
                seen.add(element);
                if (bestFrameFor(element, values, frames)?.encoding) continue;
                if (siblingSeries(element, elements)) continue;
                // A drawn line whose vertices this linter cannot read — a curve, a relative path —
                // is not evidence of anything, and saying so would put noise on correct work.
                if (element.querySelector('path')) continue;
                // A figure the graphic writes out is copy, and the plan's text check covers it —
                // but an element that renders neither shapes nor words is carrying a declaration
                // for something drawn somewhere else, which is the declaration going unchecked.
                if (
                    shapeChildren(element, elements).length === 0 &&
                    rendersText(element, elements)
                ) {
                    continue;
                }
                messages.push(
                    `${describeElement(element)} declares ${values.join(', ')}, but nothing ` +
                        `inside it draws ${values.length} shapes to match — ` +
                        `${
                            values.length === 1
                                ? 'a lone figure has nothing to be in proportion to'
                                : 'no set of shapes in it lines up with the figures'
                        }. Put the whole series on one container with one shape (or one path ` +
                        `vertex) per figure and derive their lengths from the array, so the ` +
                        `drawing can be held to the declaration.`,
                );
            }
        }
        return messages;
    },
};

export const CORE_DATA_LINT_RULES: RuntimeLintRule[] = [
    chartValuesMismatch,
    chartValuesMissing,
    chartValuesUnverifiable,
];
